import React, { useState, useEffect, forwardRef } from "react";
import { GeoJSON } from "react-leaflet";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import L from "leaflet";
import {
  INITIAL_LEGENDA,
  renderToFeaturesAttribution,
  changeHierarkiExistingToHierarkiModel,
  GEOJSON_STYLES,
  checkHollowMaps,
  uppercaseFirstLetter,
  DEFAULT_GEOJSON_STYLES,
  CHOOSE_PENGGUNAAN_TANAH,
  SIG_TIPE_BASED_BIDANG,
} from "~/utils";
import {
  setBidangResult,
  setLegendNow,
  loadingRenderMap,
  setLegendaArr,
  resetBidangHierarki,
  setTriggerRequest,
} from "~/redux/actions";
import MarkerCluster from "./MarkerCluster";
import LoadingGlobal from "./LoadingGlobal";
import request from "~/utils/request";

const DynamicLayer = forwardRef(
  (
    {
      bidang,
      url,
      onFeatureClick,
      onDataLoaded,
      filter,
      defaultStyle,
      isTriggerByReducer,
    },
    ref
  ) => {
    const dispatch = useDispatch();

    const { triggerRequest } = useSelector((state) => state.Map);

    const [loading, setLoading] = useState(false);
    const [emptyData, setEmptyData] = useState(false);
    const [geoJSONData, setGeoJSONData] = useState(null);
    const [legendaWithMeta, setLegendaWithMeta] = useState(null);
    const [activeFeatureLayer, setActiveFeatureLayer] = useState(null);

    const { search: urlSearch, pathname } = useLocation();

    const isBidangMaps = pathname === "/peta-bidang";
    const tipeQuery = new URLSearchParams(urlSearch).get("tipe");

    const isTematikMaps = geoJSONData?.features?.[0]?.properties?.tipe?.tematik;

    const { desa } = useSelector((state) => state.Auth);
    const {
      hollowMaps,
      stylesValue,
      isResetBidangHierarki,
      sigTipeData,
      showPetaJalan,
    } = useSelector((state) => state.Map);

    const formatStyleLayer = (features, legenda) => {
      if (legenda) {
        const featuresMapper = features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            properties: {
              ...feature.properties.properties,
              fillColor: legenda.find(
                (elem) => elem.value === feature?.properties?.[tipeQuery]
              )?.colour,
            },
          },
        }));

        return featuresMapper;
      } else {
        const getCurrentHierarki = changeHierarkiExistingToHierarkiModel(
          tipeQuery,
          desa?.hierarki
        );

        const getStyleLayerDasar = INITIAL_LEGENDA.DASAR?.find(
          (feature) => feature.ket_color === getCurrentHierarki
        );

        const someIsLayerTematik = features?.some(
          (feature) => feature.properties.tipe.tematik
        );

        const mapperFeaturesDasar = features?.map((feature) => {
          const haveStyleKeyInProperties = Object.entries(
            feature.properties.properties
          ).some(([key]) => GEOJSON_STYLES.includes(key));

          if (!haveStyleKeyInProperties) {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                properties: {
                  ...feature.properties.properties,
                  ...getStyleLayerDasar,
                  jenis: uppercaseFirstLetter(tipeQuery),
                },
              },
            };
          }

          return feature;
        });

        if (someIsLayerTematik) {
          return features;
        }

        return mapperFeaturesDasar;
      }
    };

    const fetchStreamBidang = async (uri) => {
      setLoading(true);
      try {
        const datas = await fetch(uri);
        const results = await datas.json();

        const features = await renderToFeaturesAttribution(results.data);

        const newFeatures = formatStyleLayer(features, results?.meta?.legenda);

        const featureColection = {
          type: "FeatureCollection",
          features: newFeatures,
        };

        if (results?.meta?.legenda) {
          const legendaMapper = results?.meta?.legenda?.map((elem) => ({
            ket_color: elem.label,
            warna: elem.colour,
            tipe: newFeatures?.[0]?.properties?.tipe?.nama,
            ...elem,
          }));

          setLegendaWithMeta(legendaMapper);
        }

        setGeoJSONData(featureColection);
        dispatch(setBidangResult(featureColection.features.length));
        onDataLoaded();
      } finally {
        setLoading(false);
      }
    };

    const handleReqData = () => {
      setLoading(true);
      request
        .get(url)
        .then(async (response) => {
          if (bidang) {
            await fetchStreamBidang(
              `${process.env.REACT_APP_API_URL}${
                response?.data?.url ?? response?.data?.data?.url
              }`
            );
          } else {
            const dataResponse = showPetaJalan
              ? response?.data?.data?.[0]
              : response.data.data;
            const metaResponse = response.data.meta;

            if (dataResponse.length !== metaResponse?.total_results) {
              setLoading(true);
              request
                .get(`${url}&per_page=${metaResponse?.total_results}`)
                .then((newResponse) => {
                  setLoading(false);
                  const newSigResponse = newResponse.data.data;
                  const features = renderToFeaturesAttribution(newSigResponse);
                  const newFeatures = formatStyleLayer(features);

                  const featureColection = {
                    type: "FeatureCollection",
                    features: newFeatures,
                  };

                  if (featureColection.features?.length <= 0) {
                    setGeoJSONData(featureColection);
                    onDataLoaded();
                    setEmptyData(true);
                  } else {
                    setGeoJSONData(featureColection);
                    onDataLoaded();
                    setEmptyData(false);
                  }
                });
            } else {
              setLoading(false);

              const sigResponse = response.data.data;
              const features = renderToFeaturesAttribution(sigResponse);
              const newFeatures = formatStyleLayer(features);

              const featureColection = {
                type: "FeatureCollection",
                features: newFeatures,
              };

              if (featureColection.features?.length <= 0) {
                setGeoJSONData(featureColection);
                onDataLoaded();
                setEmptyData(true);
              } else {
                setGeoJSONData(featureColection);
                onDataLoaded();
                setEmptyData(false);
              }
            }
          }
        })
        .catch(() => setLoading(false))
        .finally(() => setActiveFeatureLayer(null));
    };

    useEffect(() => {
      if (url && !isTriggerByReducer) {
        handleReqData();
      }
    }, [url, isTriggerByReducer]);

    useEffect(() => {
      if (isTriggerByReducer && triggerRequest) {
        handleReqData();
      }

      return () => dispatch(setTriggerRequest(false));
    }, [isTriggerByReducer, triggerRequest]);

    const onEachFeature = (feature, layer) => {
      layer.on({
        mouseover: function (e) {
          if (layer) {
            let layer = e.target;

            layer
              .bindTooltip(String(feature.properties.properties.nama))
              .openTooltip();
          }
        },

        click: function (e) {
          let layer = e.target;

          // const centroid = layer?.getCenter() || layer?.getLatLng();

          setActiveFeatureLayer(feature);
          onFeatureClick(feature, layer);
        },
      });
    };

    const createFeatureStyle = (feature) => {
      const isActiveLayer =
        JSON.stringify(feature) === JSON.stringify(activeFeatureLayer);

      const filterPropertiesWithGeoStyle = Object.fromEntries(
        Object.entries(feature.properties.properties).filter(([key]) =>
          GEOJSON_STYLES.includes(key)
        )
      );

      if (legendaWithMeta) {
        const isLegendWithMeta = legendaWithMeta?.find((legend) => {
          const isKetColor =
            legend.ket_color === "Data Kosong" ? "-" : legend?.ket_color;

          return (
            isKetColor?.toLowerCase() ===
            feature?.properties?.properties?.klasifikasi?.toLowerCase()
          );
        });

        return checkHollowMaps(
          hollowMaps,
          {
            ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
            fillColor: isLegendWithMeta?.warna,
          },
          feature?.geometry?.type,
          isActiveLayer,
          feature?.properties?.tipe?.nama
        );
      }

      if (isBidangMaps) {
        const checkIsKomoditas =
          feature?.properties?.penggunaan_tanah ===
          CHOOSE_PENGGUNAAN_TANAH.PERTANIAN
            ? feature?.properties?.komoditas
            : true;
        const checkisPenggunaanTanah =
          feature?.properties?.penggunaan_tanah ===
          CHOOSE_PENGGUNAAN_TANAH.PERTANIAN
            ? true
            : feature?.properties?.gambar_depan;

        const checkSubdesa = desa?.subdesa
          ? feature?.properties?.subdesa
          : true;
        const checkSubSubDesa = desa?.subsubdesa
          ? feature?.properties?.subsubdesa
          : true;
        const checkRW = desa?.rw ? feature?.properties?.rw : true;
        const checkRT = desa?.rt ? feature?.properties?.rt : true;

        // terintegrasi
        if (
          feature?.properties?.jumlah_kepemilikan > 0 &&
          feature?.properties?.jumlah_kepenguasaan > 0 &&
          feature?.properties?.pemanfaatan_tanah &&
          feature?.properties?.penggunaan_tanah &&
          feature?.properties?.status_hak &&
          checkIsKomoditas &&
          checkSubdesa &&
          checkSubSubDesa &&
          checkRW &&
          checkRT &&
          checkisPenggunaanTanah
        ) {
          return checkHollowMaps(
            hollowMaps,
            {
              ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
              ...INITIAL_LEGENDA.INTEGRATION.TERINTEGRASI,
            },
            feature?.geometry?.type,
            isActiveLayer,
            feature?.properties?.tipe?.nama
          );
        } else if (
          feature?.properties?.jumlah_kepemilikan > 0 ||
          feature?.properties?.jumlah_kepenguasaan > 0 ||
          feature?.properties?.pemanfaatan_tanah ||
          feature?.properties?.penggunaan_tanah ||
          feature?.properties?.status_hak ||
          checkIsKomoditas ||
          checkSubdesa ||
          checkSubSubDesa ||
          checkRW ||
          checkRT ||
          checkisPenggunaanTanah
        ) {
          return checkHollowMaps(
            hollowMaps,
            {
              ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
              ...INITIAL_LEGENDA.INTEGRATION.BELUM_LENGKAP,
            },
            feature?.geometry?.type,
            isActiveLayer,
            feature?.properties?.tipe?.nama
          );
        } else {
          return checkHollowMaps(
            hollowMaps,
            {
              ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
              ...INITIAL_LEGENDA.INTEGRATION.BELUM_TERINTEGRASI,
            },
            feature?.geometry?.type,
            isActiveLayer,
            feature?.properties?.tipe?.nama
          );
        }
      }

      if (isTematikMaps) {
        const clasificationName =
          feature?.properties?.properties?.klasifikasi_atribut_name;
        const primaryField =
          feature?.properties?.properties?.[clasificationName];

        const findClasificationExisting =
          feature?.properties?.properties?.klasifikasi_atribut?.find(
            (classification) => classification.nama === String(primaryField)
          );

        return checkHollowMaps(
          hollowMaps,
          {
            ...findClasificationExisting,
            ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_WITH_FILL_COLOR,
          },
          feature?.geometry?.type,
          isActiveLayer,
          feature?.properties?.tipe?.nama
        );
      }

      if (defaultStyle) {
        return checkHollowMaps(
          hollowMaps,
          defaultStyle,
          feature?.geometry?.type,
          isActiveLayer,
          feature?.properties?.tipe?.nama
        );
      }

      return checkHollowMaps(
        hollowMaps,
        {
          ...filterPropertiesWithGeoStyle,
          ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_WITH_FILL_COLOR,
        },
        feature?.geometry?.type,
        isActiveLayer,
        feature?.properties?.tipe?.nama
      );
    };

    const pointToLayer = (feature, latlng) => {
      const clasificationName =
        feature?.properties?.properties?.klasifikasi_atribut_name;
      const primaryField = feature?.properties?.properties?.[clasificationName];

      const findClasificationExisting =
        feature?.properties?.properties?.klasifikasi_atribut?.find(
          (classification) => classification.nama === primaryField
        );

      const markerColor = findClasificationExisting?.fillColor;

      const icon = L.AwesomeMarkers.icon({
        markerColor,
      });

      const featureIcon = L.marker(latlng, { icon });

      return featureIcon;
    };

    useEffect(() => {
      if (geoJSONData && !isBidangMaps && sigTipeData) {
        if (isTematikMaps || legendaWithMeta) {
          const clasificationLegend =
            sigTipeData?.klasifikasi?.klasifikasi_atribut;

          const clasificationLegendMapper = clasificationLegend?.map(
            (clasification) => ({
              warna: clasification?.fillColor,
              ket_color: `${clasification?.nama} ${
                sigTipeData?.has_summary
                  ? `(${clasification?.[sigTipeData?.jenis_satuan]} ${
                      sigTipeData?.nama_satuan
                    })`
                  : ""
              }`,
            })
          );

          if (legendaWithMeta) {
            dispatch(
              setLegendaArr(
                legendaWithMeta?.map((legend) => ({
                  ket_color: `${legend.ket_color} ${
                    sigTipeData?.has_summary &&
                    typeof legend?.[sigTipeData?.jenis_satuan] !== "undefined"
                      ? `(${legend?.[sigTipeData?.jenis_satuan] ?? "0"} ${
                          sigTipeData?.nama_satuan
                        })`
                      : ""
                  }`,
                  tipe: legend.tipe ?? null,
                  warna: legend.warna,
                }))
              )
            );
          } else {
            dispatch(setLegendaArr(clasificationLegendMapper));
          }
        } else {
          geoJSONData.features.map((feature) => {
            if (feature.geometry.type === "LineString") {
              return dispatch(
                setLegendNow({
                  ket_color:
                    feature.properties.properties.jenis ??
                    feature?.properties?.properties?.nama,
                  warna: feature.properties.properties.color,
                })
              );
            }

            return dispatch(
              setLegendNow({
                ket_color:
                  feature.properties.properties.jenis ??
                  feature?.properties?.properties?.nama,
                warna: feature.properties.properties.fillColor,
              })
            );
          });
        }
      }
    }, [geoJSONData, sigTipeData, legendaWithMeta]);

    useEffect(() => {
      if (isBidangMaps) {
        INITIAL_LEGENDA.INTEGRATION.LIST.map((legendIntegration) =>
          dispatch(
            setLegendNow({
              ket_color: legendIntegration.ket_warna,
              warna: legendIntegration.warna,
            })
          )
        );
      }
    }, [geoJSONData]);

    useEffect(() => {
      if (stylesValue) {
        const newGeoJSONData = {
          ...geoJSONData,
          features: geoJSONData?.features?.map((feature) => {
            if (isTematikMaps) {
              if (feature.properties.id === stylesValue.id) {
                return {
                  ...feature,
                  properties: {
                    ...feature.properties,
                    properties: {
                      ...feature.properties.properties,
                      [feature.properties.properties.klasifikasi_atribut_name]:
                        stylesValue?.[
                          feature.properties.properties.klasifikasi_atribut_name
                        ],
                    },
                  },
                };
              }

              return feature;
            } else {
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  properties: {
                    ...feature.properties.properties,
                    ...stylesValue,
                  },
                },
              };
            }
          }),
        };

        setGeoJSONData(newGeoJSONData);
      }
    }, [stylesValue]);

    useEffect(() => {
      dispatch(loadingRenderMap(loading));
    }, [loading]);

    useEffect(() => {
      const conditionalResetLayer =
        pathname === "/peta-bidang" ||
        SIG_TIPE_BASED_BIDANG.includes(tipeQuery);

      if (isResetBidangHierarki && conditionalResetLayer) {
        setGeoJSONData(null);
      }

      return () => dispatch(resetBidangHierarki(false));
    }, [isResetBidangHierarki]);

    if (loading) {
      return <LoadingGlobal />;
    }

    if (!geoJSONData || emptyData) {
      return <></>;
    }

    const uuidV4 = uuidv4();

    return (
      <MarkerCluster>
        <GeoJSON
          ref={ref}
          key={filter ? uuidV4 : null}
          data={geoJSONData}
          style={createFeatureStyle}
          pointToLayer={pointToLayer}
          onEachFeature={onEachFeature}
        />
      </MarkerCluster>
    );
  }
);

export default DynamicLayer;

DynamicLayer.defaultProps = {
  onFeatureClick: () => {},
  onDataLoaded: () => {},
  defaultStyle: null,
};

DynamicLayer.propTypes = {
  url: PropTypes.string,
  onFeatureClick: PropTypes.func.isRequired,
  bidang: PropTypes.bool,
  sigWithBidang: PropTypes.bool,
  defaultStyle: PropTypes.any,
  isBasedBidang: PropTypes.bool,
};
